import { QuestionMark } from '@mui/icons-material';
import { Box, ListItem, ListItemButton, ListItemText, Stack, Typography } from '@mui/material';
import { LookupInput, UserAvatar } from 'components';
import { ApplicationUser, UserStatusType, useFindAllUsersQuery, useGetCurrentUserQuery } from 'gql';
import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { GenericCustomFieldInputProps } from './CustomFieldInput';
import deburr from 'lodash/deburr';

type UserLookupUser = Pick<ApplicationUser, 'id' | 'displayName' | 'email'>;

const UserListItem: React.FC<{
  user: UserLookupUser;
  selected?: boolean;
  onClick?: () => void;
}> = ({ user, selected, onClick }) => {

  return <Box sx={{ backgroundColor: selected ? 'action.hover' : undefined }}>
    <ListItem
      key={user.id}
      disablePadding
    >
      <ListItemButton onClick={onClick}>
        <UserAvatar displayName={user.displayName} size='medium' />

        <ListItemText
          primary={user.displayName}
          secondary={user.email}
          sx={{ ml: 1 }}
        />
      </ListItemButton>
    </ListItem>
  </Box>;
};

interface Props extends GenericCustomFieldInputProps {
  multiple?: boolean;
  teamIdFilter?: number;
  includeMe?: boolean;
  includeInactive?: boolean;
  anchor?: 'left' | 'right';
}

export const UserLookupInput: React.FC<Props> = ({ value, onChange, multiple, teamIdFilter, error, disabled, helperText, label, required, includeMe, includeInactive, anchor }) => {
  const { formatMessage } = useIntl();

  const teamFilter = useMemo(() => (
    teamIdFilter ? { teams: { some: { id: { eq: teamIdFilter } } } } : {}
  ), [teamIdFilter]);

  const { data: whoAmIQuery } = useGetCurrentUserQuery();
  const me = whoAmIQuery?.me;

  const includeMeFilter = useMemo(() => (
    !includeMe ? { id: { neq: me?.id } } : {}
  ), [includeMe, me?.id]);

  const filters = useMemo(() => ([
    ...(includeInactive ? [] : [{ status: { neq: UserStatusType.Inactive } }]),
    includeMeFilter,
    teamFilter
  ]), [includeInactive, includeMeFilter, teamFilter]);

  const { data: userData, isFetching } = useFindAllUsersQuery({
    filter: { and: filters }
  });

  const users = useMemo(() => userData?.users ?? [], [userData?.users]);

  const selectedIds = useMemo(() => value?.split(',').filter(Boolean) ?? [], [value]);

  useEffect(() => {
    if (teamIdFilter && !isFetching) {
      onChange(users.filter(u => selectedIds.includes(u.id)).map(u => u.id).join(','));
    }
  }, [isFetching, onChange, selectedIds, teamIdFilter, users]);

  const handleIndividualsChange = (ids: string[]) => {
    onChange(ids.filter(Boolean).join(','));
  };

  return (
    <LookupInput
      anchor={anchor}
      selectedValues={users.filter(user => selectedIds.includes(user.id))}
      availableValues={includeMe ? users : users.filter(user => user.id !== me?.id)}
      getKey={user => user.id}
      getLabel={user => user.displayName}
      renderAsChips
      onChange={users => handleIndividualsChange(users.map(user => user.id))}
      renderListItem={(user, selected, onClick) => (
        <UserListItem key={user.id} user={user} selected={selected} onClick={onClick} />
      )}
      getChipProps={user => ({
        avatar: (<UserAvatar key={user.id} displayName={user.displayName} size='tiny' />),
        sx: {
          my: 0.5
        }
      })}

      renderEmptyState={() => (
        <Stack width='100%' alignItems='center' spacing={2} mt={3}>
          <QuestionMark />
          <Typography>
            {formatMessage({ id: 'No users found' })}
          </Typography>
        </Stack>
      )}

      disabled={disabled}
      label={label}
      required={required}
      error={error}
      multiple={multiple}
      helperText={helperText}
      loading={isFetching}
      searchFilter={(user, search) => deburr(user.displayName).toLowerCase().includes(deburr(search).toLowerCase())}
      SelectProps={{
        sx: {
          '& .MuiSelect-select': {
            whiteSpace: 'initial',
            px: 2
          }
        }
      }}
    />
  );
};